/** `document.title` composition — `[<object> | ]<page>[ · <section>] | Ops`.
 *
 *  Two writers contribute, and neither knows the whole story:
 *
 *  - `<DocumentTitle>` (in `RootLayout`, inside the data router) reads the
 *    matched route's `handle` and supplies the page name, the record id and
 *    whether the URL carries a section segment.
 *  - `<PageHeader>` supplies the record's display name and the active tab
 *    label — it is deep in the tree and must NOT use data-router-only hooks
 *    (`useMatches` throws under a plain `<MemoryRouter>`, which ~98 test files
 *    use), so it publishes plain strings and knows nothing about routes.
 *
 *  This store therefore holds raw INPUTS, not decisions, and `formatTitle`
 *  makes every decision in one pure place. That is what lets the two writers
 *  land in either order — React flushes child effects before parent ones, so
 *  `PageHeader` usually writes first.
 *
 *  State is keyed by pathname: a write for a different path resets everything,
 *  so a page that renders no `PageHeader` can never inherit the previous
 *  page's record name. The invariant that makes this safe — and `lessons.md`
 *  records this console losing exactly this race in `TenantScopeUrlSync` — is
 *  that **every writer takes its path from the router in the same render that
 *  schedules the effect**, never from async data. Pathname is then monotonic
 *  across commits and a late write cannot resurrect a previous page.
 */

/** Trailing segment of every title. */
export const TITLE_SUFFIX = 'Ops';

/** Shown only if a route ships without a `handle` — the coverage test in
 *  `routes.title.test.ts` is what keeps this unreachable. */
export const FALLBACK_TITLE = 'Ops Console';

/** The `handle` every route in `routes.tsx` declares. */
export interface RouteTitleHandle {
  /** The `<page>` segment: plural for list pages ('Bookings'), singular for
   *  record pages ('Booking'). Mirrors the page's visible `<h5>`. */
  title?: string;
  /** This route shows ONE record and its `PageHeader` title IS that record.
   *  Mutually exclusive with `recordLabel`. */
  detail?: boolean;
  /** For a grid whose deep-linked drawer/dialog is route-driven: the record is
   *  named `<recordLabel> #<id>` straight from the URL, with no page change.
   *  Mutually exclusive with `detail`. */
  recordLabel?: string;
  /** Route param holding the record id. Default `id`; `*` for splat routes. */
  idParam?: string;
  /** Route param carrying the active tab/step. Its presence in the URL is what
   *  admits `PageHeader`'s `section` — a default tab has no URL segment, so
   *  `/bookings/12` must not read `Booking · Overview`. */
  sectionParam?: string;
  /** A `<Navigate>` route: never painted, deliberately title-less. */
  redirect?: boolean;
}

/** What `<DocumentTitle>` knows (the route side). */
export interface RouteTitleInput {
  page: string | null;
  id: string | null;
  detail: boolean;
  recordLabel: string | null;
  hasSection: boolean;
}

/** What `<PageHeader>` knows (the page side). */
export interface HeaderTitleInput {
  label: string | null;
  section: string | null;
}

export type TitleInputs = RouteTitleInput & HeaderTitleInput;

const EMPTY: TitleInputs = {
  page: null,
  id: null,
  detail: false,
  recordLabel: null,
  hasSection: false,
  label: null,
  section: null,
};

const eq = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

const clean = (s: string | null | undefined): string | null => s?.trim() || null;

/** The id as it reads in a title: `#12` for integer keys, the bare value for
 *  everything else. */
function idSuffix(input: TitleInputs): string | null {
  const id = clean(input.id);
  if (!id) return null;
  if (/^\d+$/.test(id)) return `#${id}`;
  // Non-integer keys (delivery-log UUIDs, composite `aeid:1234` ext-ids) are
  // noise next to a record that already has a name, but they are the ONLY
  // identity a `recordLabel` route has.
  return input.recordLabel ? id : null;
}

/** The record's name, or null when this route shows no single record. */
function recordName(input: TitleInputs): string | null {
  if (input.detail) return clean(input.label);
  // A `recordLabel` route is a grid until its drawer is deep-linked.
  return clean(input.id) ? clean(input.recordLabel) : null;
}

/**
 * Compose the title from whatever both writers have contributed so far.
 *
 * When the record has no name distinct from the page — it hasn't loaded yet,
 * or the page's own fallback is `Booking #12` / `Proposal #123` — the id folds
 * into the page segment instead of repeating it: `Booking #12 | Ops`, never
 * `Booking #12 | Booking | Ops`.
 */
export function formatTitle(input: TitleInputs): string {
  const page = clean(input.page);
  if (!page) return FALLBACK_TITLE;

  const suffix = idSuffix(input);
  let name = recordName(input);
  // Strip an id the page already put in its own title, so it is never doubled.
  if (name && suffix && name.endsWith(suffix)) {
    name = clean(name.slice(0, -suffix.length));
  }

  const distinct = name && !eq(name, page) ? name : null;
  const object = distinct ? [distinct, suffix].filter(Boolean).join(' ') : null;
  const pageSegment = distinct ? page : [page, suffix].filter(Boolean).join(' ');

  // A section only counts when the URL says so, and `Blacklists · Blacklists`
  // helps nobody.
  const section = clean(input.section);
  const showSection = input.hasSection && section && !eq(section, page) ? section : null;

  return [object, [pageSegment, showSection].filter(Boolean).join(' · '), TITLE_SUFFIX]
    .filter(Boolean)
    .join(' | ');
}

let state: TitleInputs & { path: string } = { path: '', ...EMPTY };

function apply(path: string, patch: Partial<TitleInputs>): void {
  if (state.path !== path) state = { path, ...EMPTY };
  state = { ...state, ...patch };
  document.title = formatTitle(state);
}

/** Called by `<DocumentTitle>` on every navigation. */
export function publishRouteTitle(path: string, route: RouteTitleInput): void {
  apply(path, route);
}

/** Called by `<PageHeader>`. A no-op for the title on routes that are neither
 *  `detail` nor tabbed — `formatTitle` ignores both fields there. */
export function publishHeaderTitle(path: string, header: HeaderTitleInput): void {
  apply(path, header);
}

/** Test seam — module state and `document.title` outlive individual tests
 *  (vitest isolates per file, not per test). */
export function resetDocumentTitle(): void {
  state = { path: '', ...EMPTY };
}

/** Test seam. */
export function documentTitleState(): Readonly<TitleInputs & { path: string }> {
  return state;
}
